import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ShoppingCart, Plus, Trash2, AlertTriangle, Package, Sparkles, Loader2, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { value: 'pantry', label: 'Pantry', icon: '🥫' },
  { value: 'fridge', label: 'Fridge', icon: '🥛' },
  { value: 'household', label: 'Household', icon: '🧹' },
  { value: 'child_supplies', label: 'Child Supplies', icon: '🎒' },
  { value: 'baby', label: 'Baby Supplies', icon: '🍼' },
  { value: 'toiletries', label: 'Toiletries', icon: '🧴' },
];

const GroceryListPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  const [newItem, setNewItem] = useState({
    name: '',
    category: 'pantry',
    quantity: '',
    where_to_buy: '',
    low_stock: false,
    restock_needed: false
  });
  
  // E7 Task B: AI Grocery List state
  const [aiLoading, setAiLoading] = useState(false);
  const [aiGroceryList, setAiGroceryList] = useState(null);
  const [aiCallId, setAiCallId] = useState(null);
  const [meals, setMeals] = useState([]);

  useEffect(() => {
    fetchItems();
    fetchMeals();
  }, []);

  const fetchMeals = async () => {
    try {
      const response = await axios.get(`${API}/meals`);
      setMeals(response.data);
    } catch (error) {
      console.error('Error fetching meals:', error);
    }
  };

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/groceries`);
      setItems(response.data);
    } catch (error) {
      console.error('Error fetching groceries:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/groceries`, newItem);
      toast.success('Item added!');
      setDialogOpen(false);
      setNewItem({ name: '', category: 'pantry', quantity: '', where_to_buy: '', low_stock: false, restock_needed: false });
      fetchItems();
    } catch (error) {
      toast.error('Failed to add item');
    }
  };

  const toggleLowStock = async (item) => {
    try {
      await axios.put(`${API}/groceries/${item.id}`, { ...item, low_stock: !item.low_stock });
      fetchItems();
    } catch (error) {
      toast.error('Failed to update item');
    }
  };

  const toggleRestockNeeded = async (item) => {
    try {
      await axios.put(`${API}/groceries/${item.id}`, { ...item, restock_needed: !item.restock_needed });
      fetchItems();
    } catch (error) {
      toast.error('Failed to update item');
    }
  };

  const deleteItem = async (id) => {
    try {
      await axios.delete(`${API}/groceries/${id}`);
      toast.success('Item removed');
      fetchItems();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  // E7 Task B: AI Grocery List Generation
  const generateAIGroceryList = async () => {
    if (meals.length === 0) {
      toast.info('Add some meals to your meal plan first');
      return;
    }
    
    setAiLoading(true);
    setAiCallId(null);
    try {
      const response = await axios.post(`${API}/ai/grocery/generate`, {
        meal_plan: meals.map(m => ({ name: m.name, day: m.day, meal_type: m.meal_type })),
        current_inventory: items.filter(i => !i.restock_needed).map(i => i.name),
        budget_hint: ''
      });
      setAiGroceryList(response.data.grocery_list);
      setAiCallId(response.data.call_id || null);
      toast.success('AI grocery list ready!');
    } catch (error) {
      toast.error('Failed to generate grocery list');
    } finally {
      setAiLoading(false);
    }
  };

  // HITL: Confirm AI action
  const confirmAIAction = async (userAction, editDelta = []) => {
    if (!aiCallId) return;
    try {
      await axios.post(`${API}/ai/confirm`, {
        call_id: aiCallId,
        user_action: userAction,
        edit_delta: editDelta
      });
    } catch (error) {
      console.error('Failed to confirm AI action:', error);
    }
  };

  // Add AI suggested items to grocery list
  const handleUseAISuggestions = async () => {
    if (!aiGroceryList?.categories) return;
    
    await confirmAIAction('accepted');
    
    // Add each item from AI suggestions
    for (const category of aiGroceryList.categories) {
      for (const item of category.items || []) {
        try {
          await axios.post(`${API}/groceries`, {
            name: item.item,
            category: getCategoryValue(category.name),
            quantity: item.quantity || '',
            where_to_buy: '',
            low_stock: item.priority === 'essential',
            restock_needed: item.priority === 'essential'
          });
        } catch (error) {
          console.error('Failed to add item:', error);
        }
      }
    }
    
    toast.success('Items added to your grocery list!');
    setAiGroceryList(null);
    setAiCallId(null);
    fetchItems();
  };

  // Dismiss AI suggestions
  const handleDismissAI = async () => {
    await confirmAIAction('ignored');
    setAiGroceryList(null);
    setAiCallId(null);
    toast.info('AI suggestions dismissed');
  };

  // Map AI category names to our category values
  const getCategoryValue = (name) => {
    const lower = name.toLowerCase();
    if (lower.includes('produce') || lower.includes('fruit') || lower.includes('vegetable')) return 'fridge';
    if (lower.includes('protein') || lower.includes('meat') || lower.includes('dairy')) return 'fridge';
    if (lower.includes('pantry') || lower.includes('grain') || lower.includes('dry')) return 'pantry';
    if (lower.includes('baby')) return 'baby';
    if (lower.includes('toiletries') || lower.includes('personal')) return 'toiletries';
    if (lower.includes('household') || lower.includes('cleaning')) return 'household';
    return 'pantry';
  };

  const filteredItems = items.filter(item => {
    if (filterCategory === 'all') return true;
    if (filterCategory === 'low_stock') return item.low_stock;
    if (filterCategory === 'restock') return item.restock_needed;
    return item.category === filterCategory;
  });

  const groupedItems = filteredItems.reduce((acc, item) => {
    const cat = item.category || 'pantry';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  const lowStockCount = items.filter(i => i.low_stock).length;
  const restockCount = items.filter(i => i.restock_needed).length;

  return (
    <div className="space-y-6" data-testid="grocery-list-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-nurture-navy flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
              <ShoppingCart className="w-5 h-5 text-green-600" />
            </div>
            Grocery List
          </h1>
          <p className="text-nurture-muted mt-1">Track household supplies and restocking needs</p>
        </div>

        <div className="flex gap-2">
          {/* E7 Task B: AI Grocery List Button */}
          <Button
            variant="outline"
            onClick={generateAIGroceryList}
            disabled={aiLoading}
            className="rounded-full"
            data-testid="ai-grocery-btn"
          >
            {aiLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4 mr-2" />
            )}
            {aiLoading ? 'Generating...' : 'AI Shopping List'}
          </Button>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-nurture-terracotta hover:bg-nurture-terracotta-hover text-white rounded-full shadow-nurture-button" data-testid="add-grocery-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Item
              </Button>
            </DialogTrigger>
          <DialogContent className="rounded-3xl">
            <DialogHeader>
              <DialogTitle className="font-heading">Add Grocery Item</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Item Name</Label>
                <Input
                  value={newItem.name}
                  onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                  placeholder="Milk"
                  required
                  className="rounded-xl"
                  data-testid="grocery-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={newItem.category} onValueChange={(v) => setNewItem({ ...newItem, category: v })}>
                    <SelectTrigger className="rounded-xl" data-testid="grocery-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.icon} {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Quantity</Label>
                  <Input
                    value={newItem.quantity}
                    onChange={(e) => setNewItem({ ...newItem, quantity: e.target.value })}
                    placeholder="2 bottles"
                    className="rounded-xl"
                    data-testid="grocery-quantity"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Where to Buy</Label>
                <Input
                  value={newItem.where_to_buy}
                  onChange={(e) => setNewItem({ ...newItem, where_to_buy: e.target.value })}
                  placeholder="Costco, Target..."
                  className="rounded-xl"
                  data-testid="grocery-where"
                />
              </div>
              <div className="flex gap-6">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={newItem.low_stock}
                    onCheckedChange={(v) => setNewItem({ ...newItem, low_stock: v })}
                    data-testid="grocery-low-stock"
                  />
                  <Label>Low Stock</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={newItem.restock_needed}
                    onCheckedChange={(v) => setNewItem({ ...newItem, restock_needed: v })}
                    data-testid="grocery-restock"
                  />
                  <Label>Restock Needed</Label>
                </div>
              </div>
              <Button type="submit" className="w-full bg-nurture-terracotta hover:bg-nurture-terracotta-hover rounded-full" data-testid="grocery-submit">
                Add Item
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="flex gap-4 flex-wrap">
        <Button
          variant={filterCategory === 'all' ? 'default' : 'outline'}
          onClick={() => setFilterCategory('all')}
          className={`rounded-full ${filterCategory === 'all' ? 'bg-nurture-navy' : ''}`}
        >
          All Items ({items.length})
        </Button>
        <Button
          variant={filterCategory === 'low_stock' ? 'default' : 'outline'}
          onClick={() => setFilterCategory('low_stock')}
          className={`rounded-full ${filterCategory === 'low_stock' ? 'bg-yellow-500' : ''}`}
        >
          <AlertTriangle className="w-4 h-4 mr-1" />
          Low Stock ({lowStockCount})
        </Button>
        <Button
          variant={filterCategory === 'restock' ? 'default' : 'outline'}
          onClick={() => setFilterCategory('restock')}
          className={`rounded-full ${filterCategory === 'restock' ? 'bg-red-500' : ''}`}
        >
          <Package className="w-4 h-4 mr-1" />
          Need Restock ({restockCount})
        </Button>
      </div>

      {/* E7 Task B: AI Grocery List Suggestions Card */}
      {aiGroceryList && aiGroceryList.categories && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="rounded-3xl border-2 border-green-200 bg-green-50/50">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center justify-between text-nurture-navy">
                <span className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-green-600" />
                  AI Shopping List
                </span>
                <Badge variant="outline" className="text-green-700">
                  {aiGroceryList.total_estimate || 'Estimate pending'}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Categories */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {aiGroceryList.categories.map((cat, idx) => (
                  <div key={idx} className="p-3 bg-white rounded-xl border border-green-100">
                    <h4 className="font-medium text-nurture-navy mb-2">{cat.name}</h4>
                    <div className="space-y-1">
                      {(cat.items || []).slice(0, 5).map((item, i) => (
                        <div key={i} className="flex items-center justify-between text-sm">
                          <span className="text-nurture-muted">{item.item}</span>
                          <span className="text-xs text-nurture-muted">{item.quantity}</span>
                        </div>
                      ))}
                      {(cat.items || []).length > 5 && (
                        <span className="text-xs text-green-600">+{cat.items.length - 5} more</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Money saving tip */}
              {aiGroceryList.money_saving_tip && (
                <p className="text-sm text-nurture-muted italic p-2 bg-yellow-50 rounded-lg">
                  💡 {aiGroceryList.money_saving_tip}
                </p>
              )}
              
              {/* HITL Actions */}
              <div className="flex items-center gap-2 pt-2 border-t border-green-200">
                <Button
                  onClick={handleUseAISuggestions}
                  className="bg-green-500 hover:bg-green-600 text-white"
                  data-testid="use-ai-grocery-btn"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Add All to List
                </Button>
                <Button
                  variant="outline"
                  onClick={handleDismissAI}
                  data-testid="dismiss-ai-grocery-btn"
                >
                  <X className="w-4 h-4 mr-2" />
                  Dismiss
                </Button>
                <span className="text-xs text-nurture-muted ml-auto">
                  Based on your meal plan
                </span>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Items Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-48 bg-nurture-cream-dark rounded-3xl animate-pulse" />
          ))}
        </div>
      ) : Object.keys(groupedItems).length === 0 ? (
        <Card className="rounded-3xl shadow-nurture">
          <CardContent className="py-16 text-center">
            <ShoppingCart className="w-16 h-16 mx-auto text-nurture-muted/30 mb-4" />
            <h3 className="text-xl font-heading font-semibold text-nurture-navy mb-2">No items yet</h3>
            <p className="text-nurture-muted mb-4">Start tracking your household supplies</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map(category => {
            const categoryItems = groupedItems[category.value];
            if (!categoryItems || categoryItems.length === 0) return null;
            
            return (
              <motion.div
                key={category.value}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card className="rounded-3xl shadow-nurture h-full">
                  <CardHeader className="pb-2">
                    <CardTitle className="font-heading text-nurture-navy flex items-center gap-2">
                      <span className="text-2xl">{category.icon}</span>
                      {category.label}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <AnimatePresence>
                        {categoryItems.map((item) => (
                          <motion.div
                            key={item.id}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 10 }}
                            className={`p-3 rounded-xl group flex items-center gap-3 ${
                              item.restock_needed ? 'bg-red-50' : 
                              item.low_stock ? 'bg-yellow-50' : 'bg-nurture-cream/50'
                            }`}
                          >
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-nurture-navy">{item.name}</p>
                              <div className="flex items-center gap-2 mt-1 flex-wrap">
                                {item.quantity && (
                                  <span className="text-xs text-nurture-muted">{item.quantity}</span>
                                )}
                                {item.low_stock && (
                                  <Badge className="text-xs bg-yellow-100 text-yellow-700">Low</Badge>
                                )}
                                {item.restock_needed && (
                                  <Badge className="text-xs bg-red-100 text-red-700">Restock</Badge>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => toggleLowStock(item)}
                                className={`h-7 w-7 ${item.low_stock ? 'text-yellow-600' : 'text-nurture-muted'}`}
                                title="Toggle Low Stock"
                              >
                                <AlertTriangle className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => deleteItem(item.id)}
                                className="h-7 w-7 text-nurture-muted hover:text-red-500"
                                data-testid={`delete-grocery-${item.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </motion.div>
                        ))}
                      </AnimatePresence>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GroceryListPage;
